<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import PanelMenu from 'primevue/panelmenu'
import { useAuthStore } from '@/stores/auth.store'
import MenuService from '@/services/menu.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
// O <Toast /> global vive no App.vue; aqui só publicamos nele.
const toast = useToast()

const nav = (path: string) => () => router.push(path)
const active = (path: string) => route.path === path ? 'menu-item-active' : undefined

/**
 * Único item do menu que não navega: baixa o PDF direto, como o `p:fileDownload` do legado.
 *
 * A falha PRECISA aparecer. Antes ela só ia para o console, então quando o relatório não vinha o
 * clique simplesmente não fazia nada — indistinguível de um botão quebrado.
 */
const imprimirListaPresenca = async () => {
  try {
    const blob = await MenuService.imprimirRelatorioPresenca()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Relatorio_Lista_Presenca.pdf')
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Sem isso o blob fica preso na memória da aba até o refresh.
    setTimeout(() => window.URL.revokeObjectURL(url), 10000)
  } catch (error: any) {
    // O endpoint responde `blob`, então o corpo do erro também chega como Blob e o motivo real
    // ("Não foi possível realizar a impressão…") ficaria escondido dentro dele.
    let detalhe = 'Não foi possível gerar a Lista de Presença.'
    const dados = error?.response?.data

    if (dados instanceof Blob) {
      try {
        detalhe = JSON.parse(await dados.text())?.mensagem ?? detalhe
      } catch {
        // Corpo não-JSON: fica a mensagem padrão.
      }
    } else if (dados?.mensagem) {
      detalhe = dados.mensagem
    }

    toast.add({ severity: 'error', summary: 'Erro', detail: detalhe, life: 5000 })
  }
}

const menuItems = computed(() => [
  {
    label: 'Home',
    icon: 'pi pi-home',
    command: nav('/'),
    class: active('/'),
  },
  {
    label: 'Beneficiários',
    icon: 'pi pi-users',
    visible: authStore.temPermissao('MPScConecteMenuBeneficiario'),
    items: [
      {
        label: 'Verifica Elegibilidade',
        icon: 'pi pi-user',
        command: nav('/protegido/beneficiario/imp/verifica-elegibilidade'),
        visible: authStore.temPermissao('MPScConecteBeneficiario_VerificaElegibilidade'),
        class: active('/protegido/beneficiario/imp/verifica-elegibilidade'),
      },
      {
        label: 'Consultar Solicitações',
        icon: 'pi pi-list',
        command: nav('/protegido/autorizacao/imp/busca-solicitacoes-online'),
        visible: authStore.temPermissao('MPScConecteBuscaSolicitacoesOnline'),
        class: active('/protegido/autorizacao/imp/busca-solicitacoes-online'),
      },
      {
        label: 'Cadastro de Prontuário',
        icon: 'pi pi-id-card',
        command: nav('/protegido/prevencao/imp/cadastro-prontuario'),
        visible: authStore.temPermissao('MPScConecteProntuario'),
        class: active('/protegido/prevencao/imp/cadastro-prontuario'),
      },
    ],
  },
  {
    label: 'Solicitações',
    icon: 'pi pi-file-plus',
    visible: authStore.temPermissao('MPScConecteMenuAutorizacao'),
    items: [
      {
        label: 'Solicitar Autorização',
        icon: 'pi pi-credit-card',
        command: nav('/protegido/autorizacao/imp/atendimento-spsadt'),
        disabled: authStore.prestador?.solicitante === 'N',
        visible: authStore.temPermissao('MPScConecte-atendimento-SolicitaProcedimento'),
        class: active('/protegido/autorizacao/imp/atendimento-spsadt'),
      },
      {
        label: 'Executar Eventos',
        icon: 'pi pi-play',
        command: nav('/protegido/autorizacao/imp/atendimento-solicitacao-online'),
        disabled: authStore.prestador?.executante === 'N',
        visible: authStore.temPermissao('MPScConecte-atendimento-ExecutaProcedimento'),
        class: active('/protegido/autorizacao/imp/atendimento-solicitacao-online'),
      },
      {
        label: 'Exec. Tratamento Seriado',
        icon: 'pi pi-calendar-clock',
        command: nav('/protegido/autorizacao/imp/atendimento-tratamento-seriado'),
        disabled: authStore.prestador?.executante === 'N',
        visible: authStore.temPermissao('MPScConecte-atendimento-ExecutaTratamentoSeriado'),
        class: active('/protegido/autorizacao/imp/atendimento-tratamento-seriado'),
      },
      {
        label: 'Solicitar Internação de RN',
        icon: 'pi pi-building',
        command: nav('/protegido/autorizacao/imp/atendimento-internacao'),
        disabled: authStore.prestador?.executante === 'N',
        visible: authStore.temPermissao('MPScConecte-atendimento-SolicitaInternacao'),
        class: active('/protegido/autorizacao/imp/atendimento-internacao'),
      },
      // Transcrição: mesmos formulários das duas opções acima, para lançamento manual de guias
      // recebidas por outro canal. Ficam aqui dentro, e não num grupo próprio, porque são o mesmo
      // ato — solicitar ao TISS. As regras de `disabled` acompanham a opção que cada uma replica.
      {
        label: 'Transcrição SP/SADT',
        icon: 'pi pi-file-edit',
        command: nav('/protegido/autorizacao/imp/transcricao-spsadt'),
        disabled: authStore.prestador?.solicitante === 'N',
        visible: authStore.temPermissao('MPScConecte-atendimento-TranscricaoSpsadt'),
        class: active('/protegido/autorizacao/imp/transcricao-spsadt'),
      },
      {
        label: 'Transcrição Internação',
        icon: 'pi pi-file-edit',
        command: nav('/protegido/autorizacao/imp/transcricao-internacao'),
        disabled: authStore.prestador?.executante === 'N',
        visible: authStore.temPermissao('MPScConecte-atendimento-TranscricaoInternacao'),
        class: active('/protegido/autorizacao/imp/transcricao-internacao'),
      },
    ],
  },
  {
    label: 'Atendimentos',
    icon: 'pi pi-folder',
    visible: authStore.temPermissao('MPScConecteMenuAtendimento'),
    items: [
      {
        label: 'Meus Atendimentos',
        icon: 'pi pi-calendar',
        command: nav('/protegido/autorizacao/imp/busca-atendimento'),
        visible: authStore.temPermissao('MPScConectePesquisarAtendimento'),
        class: active('/protegido/autorizacao/imp/busca-atendimento'),
      },
      {
        label: 'Minhas Internações',
        icon: 'pi pi-building',
        command: nav('/protegido/autorizacao/imp/busca-internacao'),
        visible: authStore.temPermissao('MPScConectePesquisarInternacao'),
        class: active('/protegido/autorizacao/imp/busca-internacao'),
      },
      {
        label: 'Autorizações Pendentes',
        icon: 'pi pi-folder-open',
        command: nav('/protegido/autorizacao/imp/busca-atendimento-solicitados'),
        visible: authStore.temPermissao('MPScConecteAtendimentosSolicitados'),
        class: active('/protegido/autorizacao/imp/busca-atendimento-solicitados'),
      },
      {
        label: 'Aguardando Justificativa',
        icon: 'pi pi-comments',
        command: nav('/protegido/autorizacao/imp/busca-justificativas'),
        visible: authStore.temPermissao('MPScConecteAguardandoJustificativa'),
        class: active('/protegido/autorizacao/imp/busca-justificativas'),
      },
      {
        label: 'Beneficiário Internado',
        icon: 'pi pi-heart',
        command: nav('/protegido/autorizacao/imp/beneficiario-internado'),
        visible: authStore.temPermissao('MPScConecteBeneficiarioInternado'),
        class: active('/protegido/autorizacao/imp/beneficiario-internado'),
      },
      {
        label: 'Resumo Mensal',
        icon: 'pi pi-chart-line',
        command: nav('/protegido/autorizacao/imp/atendimento-resumo-mensal'),
        class: active('/protegido/autorizacao/imp/atendimento-resumo-mensal'),
      },
    ],
  },
  {
    label: 'Faturamento',
    icon: 'pi pi-dollar',
    visible: authStore.temPermissao('MPScConecteMenuFaturamento'),
    items: [
      {
        label: 'Consultar Guias Faturar',
        icon: 'pi pi-search',
        command: nav('/protegido/faturamento/imp/busca-guias-faturar'),
        visible: authStore.temPermissao('MPScConecteConsultaGuiasFaturar'),
        class: active('/protegido/faturamento/imp/busca-guias-faturar'),
      },
      {
        label: 'Consulta de lotes',
        icon: 'pi pi-inbox',
        command: nav('/protegido/faturamento/imp/busca-lotes-faturar'),
        visible: authStore.temPermissao('MPScConecteConsultaLote'),
        class: active('/protegido/faturamento/imp/busca-lotes-faturar'),
      },
      {
        // Atenção: o grupo "Faturamento" já é fechado por MPScConecteMenuFaturamento, então quem
        // precisa deste item precisa das DUAS permissões — a do grupo e esta.
        label: 'Associar guias sem lote',
        icon: 'pi pi-link',
        command: nav('/protegido/faturamento/imp/guias-sem-lote'),
        visible: authStore.temPermissao('MPScConecteAssociarGuiasSemLote'),
        class: active('/protegido/faturamento/imp/guias-sem-lote'),
      },
    ],
  },
  {
    label: 'Favoritos',
    icon: 'pi pi-star',
    visible: authStore.temPermissao('MPScConecteMenuEventosFavoritos'),
    items: [
      {
        label: 'Criar lista Eventos',
        icon: 'pi pi-plus-circle',
        command: nav('/protegido/prestador/imp/cria-eventos-favoritos'),
        visible: authStore.temPermissao('MPScConecteCriarListaEventosFavoritos'),
        class: active('/protegido/prestador/imp/cria-eventos-favoritos'),
      },
      {
        label: 'Listar Favoritos',
        icon: 'pi pi-list',
        command: nav('/protegido/prestador/imp/listas-eventos-favoritos'),
        visible: authStore.temPermissao('MPScConecteListarEventosFavoritos'),
        class: active('/protegido/prestador/imp/listas-eventos-favoritos'),
      },
    ],
  },
  {
    label: 'Relatórios',
    icon: 'pi pi-print',
    // O legado fecha o submenu INTEIRO nesta permissão (menu.xhtml: rendered do idMenuRelatorio),
    // e não só a Lista de Presença. Consequência da regra, mantida: quem tiver apenas
    // MPScConecteAcompanhamentoLotePorExec não vê o grupo, e portanto não vê o relatório de
    // Faturamento. "Lista de Presença" segue sem `visible` próprio, como no legado, porque herda
    // a visibilidade do grupo.
    visible: authStore.temPermissao('MPScConecteListaDePresenca'),
    items: [
      {
        label: 'Lista de Presença',
        icon: 'pi pi-print',
        command: () => imprimirListaPresenca(),
      },
      {
        label: 'Faturamento',
        icon: 'pi pi-dollar',
        command: nav('/protegido/faturamento/imp/lotes-por-executante'),
        visible: authStore.temPermissao('MPScConecteAcompanhamentoLotePorExec'),
        class: active('/protegido/faturamento/imp/lotes-por-executante'),
      },
    ],
  },
  {
    label: 'Avisos',
    icon: 'pi pi-bell',
    visible: authStore.temPermissao('MPScConecteAvisos'),
    items: [
      {
        label: 'Cadastro de Avisos',
        icon: 'pi pi-plus',
        command: nav('/protegido/avisos/imp/cadastro-avisos'),
        visible: authStore.temPermissao('MPScConecteCadastroDeAvisos'),
        class: active('/protegido/avisos/imp/cadastro-avisos'),
      },
      {
        label: 'Avisos',
        icon: 'pi pi-bell',
        command: nav('/protegido/avisos/imp/avisos'),
        visible: authStore.temPermissao('MPScConecteListagemDeAvisos'),
        class: active('/protegido/avisos/imp/avisos'),
      },
    ],
  },
  {
    label: 'Administração',
    icon: 'pi pi-shield',
    visible: authStore.temPermissao('MPScConecteAdministrador'),
    command: nav('/protegido/admin'),
    class: active('/protegido/admin'),
  },
])
</script>

<template>
  <form id="menuform" @submit.prevent>
    <div :class="['layout-menu', authStore.menuConfig?.menuClass ?? '']">
      <div class="nano">
        <div class="nano-content menu-scroll-content">

          <!-- Perfil Inline -->
          <template v-if="authStore.menuConfig?.profileMode === 'inline' || true">
            <div class="profile-container">
              <div class="profile-image">
                <i class="pi pi-user text-2xl text-600" />
              </div>
              <div class="profile-info">
                <span class="profile-name">{{ authStore.usuario?.nome || 'Usuário' }}</span>
                <span class="profile-role text-500 text-xs">{{ authStore.prestador?.nome || 'Prestador' }}</span>
              </div>
            </div>
            <div class="border-bottom-1 border-300 mb-3 mx-3"></div>
          </template>

          <PanelMenu :model="menuItems" class="w-full" />

        </div>
      </div>
    </div>
  </form>
</template>

<style scoped>
.layout-menu {
  width: 100%;
  height: 100%;
}

.nano {
  height: 100%;
  overflow: hidden;
}

.nano-content {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}

.profile-container {
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.profile-image {
  width: 64px;
  height: 64px;
  margin-bottom: 0.75rem;
  border-radius: 50%;
  background-color: var(--surface-200, #f8f9fa);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--cassems-blue, #0056b3);
}

.profile-info {
  display: flex;
  flex-direction: column;
}

.profile-name {
  font-weight: 600;
  color: var(--text-color, #333);
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.profile-role {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

:deep(.p-panelmenu .p-panelmenu-header-content) {
  background: transparent;
  border: none;
}

:deep(.p-panelmenu .p-panelmenu-header-action) {
  padding: 0.75rem 1rem;
  color: #555;
  transition: all 0.2s;
}

:deep(.p-panelmenu .p-panelmenu-header-action:hover) {
  background: var(--c-surface-hover);
  color: var(--cassems-blue);
}

:deep(.p-menuitem-icon) {
  margin-right: 0.75rem;
  color: var(--cassems-blue);
}

/* Estado ativo.
   Antes era uma barra azul de 4px à esquerda, num azul (#334195) que nem era o da marca.
   Item de menu ativo se resolve com fundo tingido + cor + peso: lê-se de relance, não compete
   com o conteúdo e não deixa a navegação com aparência de aviso. */
:deep(.p-panelmenu-item.menu-item-active > .p-panelmenu-header > .p-panelmenu-header-content > .p-panelmenu-header-action),
:deep(.p-menuitem.menu-item-active > .p-menuitem-content) {
  background-color: var(--c-primary-50);
}

:deep(.p-panelmenu-item.menu-item-active > .p-panelmenu-header > .p-panelmenu-header-content > .p-panelmenu-header-action),
:deep(.p-menuitem.menu-item-active .p-menuitem-link),
:deep(.p-menuitem.menu-item-active .p-menuitem-icon),
:deep(.p-menuitem.menu-item-active .p-menuitem-text) {
  color: var(--c-brand);
  font-weight: var(--c-weight-semibold);
}
</style>
